import React, { useContext, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  DiamondRounded as MetalIcon,
  WarehouseRounded as FloorIcon,
  CategoryRounded as ItemTypeIcon,
} from "@mui/icons-material";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import PawnMetalTypeMaster from "./PawnMetalTypeMaster";
import PawnFloorMaster from "./PawnFloorMaster";
import PawnItemTypeMaster from "./PawnItemTypeMaster";
import { reportSwitcherStripSx } from "./reportLayout";
import PawnNavButtons from "./pawnNavButtons";

// ─── Blue/White Design Tokens (shared across the pawn master screens) ────────
const BLUE = "#2563EB";
const INK = "#0F172A";
const INK_MUTED = "#64748B";
const BORDER = "#E2E8F0";
const BG_ALT = "#F1F5F9";

// The masters, keyed by the `?type=` query param. Each entry carries the
// heading shown at the top and the body component that renders in embedded mode
// (its own header hidden — this screen supplies it).
const MASTERS = {
  metal: {
    label: "Metal Type",
    title: "Metal Type Master",
    subtitle: "Metals, their default purity and barcode prefixes",
    icon: <MetalIcon />,
    Component: PawnMetalTypeMaster,
  },
  // What each metal's pieces are pledged as. Feeds the item dropdown on the
  // Payment screen's ornament row.
  item: {
    label: "Item Type",
    title: "Item Type Master",
    subtitle: "Chain, bangle, ring… what each metal is pledged as",
    icon: <ItemTypeIcon />,
    Component: PawnItemTypeMaster,
  },
  floor: {
    label: "Floor",
    title: "Floor Master",
    subtitle: "Manage locations, floors & shelves",
    icon: <FloorIcon />,
    Component: PawnFloorMaster,
  },
};

// Old links (and the legacy /pawn/purity-master redirect) still say ?type=purity.
// Purities are no longer a master of their own — a rate belongs to the metal and
// an ornament carries its own percentage — so those links land on the metal tab.
const TYPE_ALIASES = { purity: "metal" };

const DEFAULT_TYPE = "metal";

const PawnMasters = () => {
  const { currentStoreInfo } = useContext(AutoTaskAuthContext) || {};
  const [searchParams, setSearchParams] = useSearchParams();

  // The active master comes from the URL (?type=), so the old per-master routes
  // can redirect straight to the right tab and links stay shareable.
  const rawType = TYPE_ALIASES[searchParams.get("type")] ||
    searchParams.get("type");
  const type = MASTERS[rawType] ? rawType : DEFAULT_TYPE;
  const active = MASTERS[type];
  const ActiveMaster = active.Component;

  const handleChange = (_e, next) => {
    if (!next || next === type) return;
    // replace: switching masters shouldn't stack browser-history entries.
    setSearchParams({ type: next }, { replace: true });
  };

  const chips = useMemo(
    () => (
      <ToggleButtonGroup
        value={type}
        exclusive
        size="small"
        onChange={handleChange}
        sx={{
          // Natural width, never `100%` — the wrapping strip scrolls sideways if
          // the chips don't fit rather than letting them widen the page (see
          // reportSwitcherStripSx).
          width: "auto",
          "& .MuiToggleButton-root": {
            textTransform: "none",
            px: { xs: 1, sm: 1.5, md: 2 },
            py: { xs: 0.6, md: 0.75 },
            fontSize: { xs: "0.72rem", sm: "0.8rem" },
            fontWeight: 700,
            color: INK_MUTED,
            borderColor: BORDER,
            gap: 0.5,
          },
          "& .Mui-selected": {
            bgcolor: `${BLUE} !important`,
            color: "#fff !important",
          },
        }}
      >
        {Object.entries(MASTERS).map(([key, m]) => (
          <ToggleButton key={key} value={key}>
            {React.cloneElement(m.icon, { sx: { fontSize: 18, mr: 0.5 } })}
            {m.label}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type],
  );

  return (
    <Box
      sx={{
        height: "100%",
        minWidth: 0,
        // Flex column so a master that wants the leftover height (Metal Type,
        // whose table scrolls internally) can take it, while one that keeps its
        // natural height (Floor) just scrolls the page.
        display: "flex",
        flexDirection: "column",
        overflowY: "auto",
        overflowX: "hidden",
        bgcolor: BG_ALT,
        p: { xs: 1.25, sm: 1.5, md: 2.5 },
      }}
    >
      {/* ── Top bar: heading + master switcher. On desktop the switcher sits at
          the right edge; on mobile it stacks ABOVE the heading. ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          gap: { xs: 1, sm: 1.5 },
          mb: { xs: 1.5, md: 2 },
          minWidth: 0,
          flexShrink: 0,
        }}
      >
        {/* Heading — order 2 on mobile (below the chips), left on desktop. */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 1, sm: 1.5 },
            order: { xs: 2, md: 1 },
            minWidth: 0,
          }}
        >
          <PawnNavButtons />
          <Box
            sx={{
              width: { xs: 36, sm: 42 },
              height: { xs: 36, sm: 42 },
              flexShrink: 0,
              borderRadius: 2,
              bgcolor: BLUE,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              boxShadow: "0 4px 12px rgba(37,99,235,0.35)",
            }}
          >
            {active.icon}
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              noWrap
              sx={{ fontWeight: 800,
                fontSize: { xs: "1.05rem", sm: "1.25rem" },
                color: INK }}
            >
              {active.title}
            </Typography>
            <Typography
              noWrap
              sx={{ fontSize: "0.75rem", color: INK_MUTED }}
            >
              {currentStoreInfo?.name
                ? `${currentStoreInfo.name} · ${active.subtitle}`
                : active.subtitle}
            </Typography>
          </Box>
        </Box>

        {/* Switcher — order 1 on mobile (top), right edge on desktop. The strip
            scrolls sideways so the chips never widen the page. */}
        <Box sx={{ order: { xs: 1, md: 2 }, ...reportSwitcherStripSx }}>
          {chips}
        </Box>
      </Box>

      {/* Active master body (header suppressed via `embedded`). Only the
          selected master is mounted, so just it fetches. */}
      <ActiveMaster embedded />
    </Box>
  );
};

export default PawnMasters;
